import React from 'react';
import { theme } from 'antd';
import { CaretDownFilled, CaretUpFilled } from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';

// Dormant for MVP along with TrendWithPeriod: period-over-period comparisons
// are post-MVP, so no tile passes a `change`. When deltas return, reword this —
// the reporting window is 30 days now, not the prototype's 14.
export const TREND_PERIOD_MESSAGE = 'Compares the selected 14-day period with the 14 days before it.';

export type TrendIndicatorProps = {
  /** Sign sets direction; zero reads as up. */
  change: number;
  /**
   * For "lower is better" metrics: a drop shows green, a rise red. The spoken
   * direction always follows the real sign.
   */
  invertSentiment?: boolean;
  size?: number;
  style?: React.CSSProperties;
};

function TrendIndicator({ change, invertSentiment = false, size, style }: TrendIndicatorProps) {
  const { token } = theme.useToken();

  const fontSize = size ?? token.fontSizeSM;
  const isUp = change >= 0;
  const isGood = invertSentiment ? !isUp : isUp;
  const color = isGood ? token.colorSuccess : token.colorError;

  return (
    <span
      aria-label={isUp ? `Increased by ${Math.abs(change)}%` : `Decreased by ${Math.abs(change)}%`}
      style={{ display: 'inline-flex', alignItems: 'center', ...style }}
    >
      <span aria-hidden="true" style={{ fontSize, fontWeight: token.fontWeightStrong, color }}>
        {isUp ? `+${change}%` : `${change}%`}
      </span>
      {isUp ? (
        <CaretUpFilled aria-hidden style={{ fontSize, color }} />
      ) : (
        <CaretDownFilled aria-hidden style={{ fontSize, color }} />
      )}
    </span>
  );
}

export function TrendWithPeriod({ change, invertSentiment }: Pick<TrendIndicatorProps, 'change' | 'invertSentiment'>) {
  return (
    <CustomTooltip
      placement="top"
      message={TREND_PERIOD_MESSAGE}
      wrapperStyle={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
    >
      <TrendIndicator change={change} invertSentiment={invertSentiment} />
    </CustomTooltip>
  );
}

export default TrendIndicator;
